/**
 * MasterScheduleBuilder.ts
 * 
 * Builds complete 1920-game schedule from all 24 Basehit team schedules.
 */

import { ScheduledGame, TeamScheduleInfo } from './ScheduleGenerator';
import { COMPLETE_MASTER_SCHEDULE } from '../data/completeMasterSchedule';

/**
 * Generate full schedule for SimForge
 */
export function generateMasterSchedule(
  teams: TeamScheduleInfo[],
  leagueId: string,
  seasonNumber: number = 0
): ScheduledGame[] {
  console.log(`⚾ MASTER SCHEDULE GENERATOR for ${teams.length} teams, season ${seasonNumber}`);
  
  if (teams.length !== 24) {
    console.warn(`⚠️ Expected 24 teams, got ${teams.length}`);
  }
  
  // Map slots to actual team IDs with rotation
  const slotToTeam = new Map<number, string>();
  teams.forEach((team, index) => {
    const slot = (index + seasonNumber) % 24;
    slotToTeam.set(slot, team.teamId);
  });
  
  // Convert to ScheduledGame format
  const games: ScheduledGame[] = [];
  let gameId = 0;
  
  // Track game number within each home-away pair series
  const seriesTracker = new Map<string, number>();

  for (const game of COMPLETE_MASTER_SCHEDULE) {
    const homeTeamId = slotToTeam.get(game.homeSlot);
    const awayTeamId = slotToTeam.get(game.awaySlot);
    
    if (!homeTeamId || !awayTeamId) {
      console.warn(`⚠️ Missing team for slots ${game.homeSlot} or ${game.awaySlot}`);
      continue;
    }

    // Assign sequential game number within each home-away pair
    const seriesKey = `${homeTeamId}-${awayTeamId}`;
    const gameNumber = (seriesTracker.get(seriesKey) || 0) + 1;
    seriesTracker.set(seriesKey, gameNumber);
    
    games.push({
      id: `ms-${gameId++}`,
      gameDay: game.day,
      homeTeamId,
      awayTeamId,
      isPlayoff: false,
      gameNumber,
    });
  }
  
  // Log stats
  logScheduleStats(games, teams);
  
  return games;
}

function logScheduleStats(games: ScheduledGame[], teams: TeamScheduleInfo[]): void {
  const stats = new Map<string, { home: number; away: number; total: number }>();
  
  teams.forEach(team => {
    stats.set(team.teamId, { home: 0, away: 0, total: 0 });
  });
  
  games.forEach(game => {
    const homeStats = stats.get(game.homeTeamId);
    const awayStats = stats.get(game.awayTeamId);
    if (homeStats) { homeStats.home++; homeStats.total++; }
    if (awayStats) { awayStats.away++; awayStats.total++; }
  });
  
  const totals = Array.from(stats.values()).map(s => s.total);
  const homes = Array.from(stats.values()).map(s => s.home);
  const aways = Array.from(stats.values()).map(s => s.away);
  
  console.log(`\n📊 Master Schedule Stats:`);
  console.log(`   Total games: ${games.length}`);
  console.log(`   Games per team: ${Math.min(...totals)}-${Math.max(...totals)}`);
  console.log(`   Home games: ${Math.min(...homes)}-${Math.max(...homes)}`);
  console.log(`   Away games: ${Math.min(...aways)}-${Math.max(...aways)}`);
  
  const expectedGames = 1920;
  if (games.length === expectedGames) {
    console.log(`   ✅ PERFECT! ${expectedGames} games as expected!`);
  } else {
    console.log(`   ⚠️ Expected ${expectedGames}, got ${games.length}`);
  }
}
